import React, { useEffect, useMemo, useRef } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SyncIcon from '@mui/icons-material/Sync';
import { Box, Chip, Stack, Typography, alpha, useTheme } from '@mui/material';
import { borderRadius, layoutGap } from '../../../theme/theme';
import type {
  OperationProgressItem,
  OperationProgressItemStatus,
  OperationProgressStepStatus,
} from './operationProgress.types';
import { getOperationProgressStepIconColor } from './operationProgressPresentation';
import { diagLog } from '../../api/grpc/webLogger';

type ProgressActivityStatus = OperationProgressItemStatus | OperationProgressStepStatus;

export const OperationProgressStateIcon: React.FC<{ status: ProgressActivityStatus }> = ({
  status,
}) => {
  const stepStatus = status === 'running' ? 'current' : status === 'conflict' ? 'error' : status;
  const color = getOperationProgressStepIconColor(stepStatus);

  if (status === 'complete') {
    return <CheckCircleOutlineIcon fontSize="small" color={color} />;
  }

  if (status === 'current' || status === 'running') {
    return <SyncIcon fontSize="small" color={color} />;
  }

  if (status === 'error' || status === 'conflict') {
    return <ErrorOutlineIcon fontSize="small" color={color} />;
  }

  return <RadioButtonUncheckedIcon fontSize="small" color={color} />;
};

const getItemStatusLabel = (status: OperationProgressItemStatus): string => {
  switch (status) {
    case 'running':
      return 'Running';
    case 'complete':
      return 'Complete';
    case 'skipped':
      return 'Skipped';
    case 'conflict':
      return 'Conflict';
    case 'error':
      return 'Failed';
    case 'pending':
    default:
      return 'Pending';
  }
};

const getItemChipColor = (status: OperationProgressItemStatus) => {
  switch (status) {
    case 'complete':
      return 'success' as const;
    case 'conflict':
      return 'warning' as const;
    case 'error':
      return 'error' as const;
    case 'running':
      return 'primary' as const;
    default:
      return 'default' as const;
  }
};

const getItemFallbackDetail = (status: OperationProgressItemStatus): string => {
  switch (status) {
    case 'complete':
      return 'Applied successfully';
    case 'skipped':
      return 'Not required';
    case 'conflict':
      return 'Conflicting Revit value';
    case 'error':
      return 'Revit rejected this parameter';
    case 'running':
      return 'Applying in Revit';
    case 'pending':
    default:
      return 'Awaiting Revit result';
  }
};

export const OperationProgressActivityList: React.FC<{
  readonly items: OperationProgressItem[];
  readonly compact?: boolean;
}> = ({ items, compact = false }) => {
  const theme = useTheme();
  const runningItemId = useMemo(
    () => items.find((item) => item.status === 'running')?.id,
    [items]
  );
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // scrollIntoView on this overflow container wedges the embedded WebView2
    // renderer (layout machinery + host GPU deadlock), freezing the whole page
    // mid-operation. Direct scrollTop assignment avoids that code path while
    // keeping the running row in view — new items append at the bottom anyway.
    const container = containerRef.current;
    if (!container) {
      return;
    }

    diagLog('progress-watchdog', `activity:scroll:start item=${runningItemId ?? 'none'}`);
    container.scrollTop = container.scrollHeight;
    diagLog('progress-watchdog', 'activity:scroll:end');
  }, [runningItemId]);

  if (items.length === 0) {
    return null;
  }

  return (
    <Stack
      ref={containerRef}
      spacing={layoutGap.micro}
      data-testid="operation-progress-item-list"
      aria-label="Parameter activity"
      sx={{ maxHeight: compact ? 180 : 260, overflowY: 'auto' }}
    >
      {items.map((item) => {
        const reason = item.error || item.detail;
        return (
          <Box
            key={item.id}
            data-testid="operation-progress-item"
            data-item-status={item.status}
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr) auto',
                sm: 'minmax(150px, 0.8fr) minmax(180px, 1.2fr) auto',
              },
              alignItems: 'center',
              gap: 1,
              minWidth: 0,
              px: 0.85,
              py: compact ? 0.4 : 0.55,
              borderRadius: `${borderRadius.sm}px`,
              border: `1px solid ${alpha(theme.palette.divider, 0.74)}`,
              backgroundColor: alpha(theme.palette.background.paper, 0.44),
            }}
          >
            <Stack direction="row" spacing={layoutGap.blend} alignItems="center" sx={{ minWidth: 0 }}>
              <OperationProgressStateIcon status={item.status} />
              <Typography variant="caption" sx={{ fontWeight: 700 }} noWrap>
                {item.label}
              </Typography>
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              title={reason || getItemFallbackDetail(item.status)}
              sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' }, minWidth: 0 }}
            >
              {reason || getItemFallbackDetail(item.status)}
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              label={getItemStatusLabel(item.status)}
              color={getItemChipColor(item.status)}
              sx={{ minWidth: 74, height: 22, '& .MuiChip-label': { fontWeight: 700 } }}
            />
          </Box>
        );
      })}
    </Stack>
  );
};
